import React, { useState, useEffect, useRef, useCallback } from 'react';
import { X, TrendingUp, BarChart3, Activity, Globe, ArrowRight } from 'lucide-react';
import { useLanguage } from '@/i18n/LanguageContext';
import { trackEvent } from '@/lib/analytics';
import './WorldCupPopup.css';

/* ── Configuration ── */
const POPUP_ENABLED = true;
const POPUP_DELAY = 2500;
const FREQ_HOURS = 24;
const URL_EN = 'https://sports.yourbrandvalue.com/world-cup-brand-index-tool.html';
const URL_ES = 'https://sports.yourbrandvalue.com/world-cup-brand-index-tool.html?lang=es';
const VER = 'v1';
const LS_KEY = 'ybv_world_cup_popup_last_seen';

/* ── Copy ── */
var enCopy = {
  eyebrow: 'NEW · WORLD CUP BRAND INDEX™',
  headline: 'Which national team leads the World Cup beyond the pitch?',
  description: 'Discover the first index analysing and comparing the brand value of World Cup national teams through sporting, digital, commercial and cultural signals.',
  ind1: '48 national teams analysed',
  ind2: 'Rankings and Brand Signals',
  ind3: 'Updated throughout the World Cup',
  cta: 'EXPLORE THE INDEX',
  micro: 'Free access · Discover which national teams turn impact into brand value',
  closeLabel: 'Close',
};

var esCopy = {
  eyebrow: 'NUEVO · WORLD CUP BRAND INDEX™',
  headline: String.fromCharCode(191) + 'Qu' + String.fromCharCode(233) + ' selecci' + String.fromCharCode(243) + 'n lidera el Mundial m' + String.fromCharCode(225) + 's all' + String.fromCharCode(225) + ' del terreno de juego?',
  description: 'Descubre el primer ' + String.fromCharCode(237) + 'ndice que analiza y compara el valor de marca de las selecciones del Mundial mediante se' + String.fromCharCode(241) + 'ales deportivas, digitales, comerciales y culturales.',
  ind1: '48 selecciones analizadas',
  ind2: 'Rankings y Brand Signals',
  ind3: 'Datos actualizados durante el Mundial',
  cta: 'EXPLORAR EL ' + String.fromCharCode(205) + 'NDICE',
  micro: 'Acceso gratuito · Descubre qu' + String.fromCharCode(233) + ' selecciones convierten mejor su impacto en valor de marca',
  closeLabel: 'Cerrar',
};

/* ── Helpers ── */
function buildUrl(lang) {
  var base = lang === 'es' ? URL_ES : URL_EN;
  try {
    var u = new URL(base);
    u.searchParams.set('utm_source', 'yourbrandvalue');
    u.searchParams.set('utm_medium', 'homepage_popup');
    u.searchParams.set('utm_campaign', 'world_cup_brand_index');
    return u.toString();
  } catch (e) {
    return base;
  }
}

function resolveLang(ctx) {
  if (ctx === 'es' || ctx === 'en') return ctx;
  try { if (navigator.language && navigator.language.startsWith('es')) return 'es'; } catch (e) { /* */ }
  return 'en';
}

function canShow() {
  if (!POPUP_ENABLED) return false;
  try {
    var raw = localStorage.getItem(LS_KEY);
    if (!raw) return true;
    var ts = parseInt(raw, 10);
    if (isNaN(ts)) return true;
    return (Date.now() - ts) / 3600000 >= FREQ_HOURS;
  } catch (e) { return true; }
}

function markSeen() {
  try { localStorage.setItem(LS_KEY, String(Date.now())); } catch (e) { /* */ }
}

function deviceType() {
  if (typeof window === 'undefined') return 'unknown';
  return window.innerWidth < 768 ? 'mobile' : window.innerWidth < 1024 ? 'tablet' : 'desktop';
}

/* ── Ranking Bars (static illustrative) ── */
function RankBars() {
  return (
    <div className="wc-popup-rank-rows">
      <div className="wc-popup-rank-row wc-popup-rank-glow">
        <span className="wc-popup-rank-pos">1</span>
        <div className="wc-popup-rank-bar-track"><div className="wc-popup-rank-bar" style={{width: '92%'}} /></div>
      </div>
      <div className="wc-popup-rank-row">
        <span className="wc-popup-rank-pos">2</span>
        <div className="wc-popup-rank-bar-track"><div className="wc-popup-rank-bar" style={{width: '87%'}} /></div>
      </div>
      <div className="wc-popup-rank-row">
        <span className="wc-popup-rank-pos">3</span>
        <div className="wc-popup-rank-bar-track"><div className="wc-popup-rank-bar" style={{width: '83%'}} /></div>
      </div>
      <div className="wc-popup-rank-row">
        <span className="wc-popup-rank-pos">4</span>
        <div className="wc-popup-rank-bar-track"><div className="wc-popup-rank-bar" style={{width: '78%'}} /></div>
      </div>
      <div className="wc-popup-rank-row">
        <span className="wc-popup-rank-pos">5</span>
        <div className="wc-popup-rank-bar-track"><div className="wc-popup-rank-bar" style={{width: '74%'}} /></div>
      </div>
    </div>
  );
}

/* ── Visual Section ── */
function VisualSection() {
  return (
    <div className="wc-popup-visual" aria-hidden="true">
      <div className="wc-popup-rank-card">
        <div className="wc-popup-rank-header">
          <BarChart3 size={14} />
          <span>Global Ranking</span>
        </div>
        <RankBars />
      </div>
      <div className="wc-popup-metrics-row">
        <div className="wc-popup-metric-card">
          <span className="wc-popup-metric-label">Brand Score</span>
          <span className="wc-popup-metric-value">87.4</span>
          <div className="wc-popup-metric-bar"><div className="wc-popup-metric-fill" style={{width: '87%'}} /></div>
        </div>
        <div className="wc-popup-metric-card">
          <span className="wc-popup-metric-label">Momentum</span>
          <div className="wc-popup-metric-trend">
            <TrendingUp size={16} className="wc-popup-trend-icon" />
            <span className="wc-popup-metric-value wc-popup-trend-up">+4.2</span>
          </div>
        </div>
        <div className="wc-popup-metric-card">
          <span className="wc-popup-metric-label">Signal</span>
          <div className="wc-popup-signal-dot-row">
            <Activity size={14} className="wc-popup-signal-icon" />
            <span className="wc-popup-signal-text">Active</span>
          </div>
        </div>
      </div>
    </div>
  );
}

/* ── Main Component ── */
export default function WorldCupPopup() {
  var langCtx = useLanguage();
  var lang = resolveLang(langCtx.lang);
  var copy = lang === 'es' ? esCopy : enCopy;
  var destUrl = buildUrl(lang);

  var stateArr = useState(false);
  var visible = stateArr[0];
  var setVisible = stateArr[1];
  var exitArr = useState(false);
  var exiting = exitArr[0];
  var setExiting = exitArr[1];
  var hasFired = useRef(false);
  var prevFocus = useRef(null);
  var modalRef = useRef(null);
  var ctaRef = useRef(null);
  var reducedMotion = useRef(false);

  useEffect(function() {
    try { reducedMotion.current = window.matchMedia('(prefers-reduced-motion: reduce)').matches; } catch (e) { /* */ }
  }, []);

  useEffect(function() {
    if (!canShow()) return;
    var t = setTimeout(function() {
      prevFocus.current = document.activeElement;
      setVisible(true);
    }, POPUP_DELAY);
    return function() { clearTimeout(t); };
  }, []);

  useEffect(function() {
    if (visible && !hasFired.current) {
      hasFired.current = true;
      trackEvent('world_cup_popup_view', {
        language: lang, destination_url: destUrl, page_path: window.location.pathname,
        device_type: deviceType(), popup_version: VER,
      });
    }
  }, [visible, lang, destUrl]);

  useEffect(function() {
    if (visible && !exiting) { document.body.style.overflow = 'hidden'; }
    else { document.body.style.overflow = ''; }
    return function() { document.body.style.overflow = ''; };
  }, [visible, exiting]);

  useEffect(function() {
    if (visible && !exiting && ctaRef.current) ctaRef.current.focus();
  }, [visible, exiting]);

  var close = useCallback(function(method) {
    markSeen();
    trackEvent('world_cup_popup_close', {
      language: lang, page_path: window.location.pathname,
      device_type: deviceType(), popup_version: VER, close_method: method,
    });
    if (reducedMotion.current) {
      setVisible(false);
      if (prevFocus.current) prevFocus.current.focus();
    } else {
      setExiting(true);
      setTimeout(function() {
        setVisible(false); setExiting(false);
        if (prevFocus.current) prevFocus.current.focus();
      }, 250);
    }
  }, [lang]);

  var handleCTA = useCallback(function() {
    markSeen();
    trackEvent('world_cup_popup_cta_click', {
      language: lang, destination_url: destUrl, page_path: window.location.pathname,
      device_type: deviceType(), popup_version: VER,
    });
  }, [lang, destUrl]);

  useEffect(function() {
    if (!visible || exiting) return;
    var handler = function(e) { if (e.key === 'Escape') close('escape'); };
    document.addEventListener('keydown', handler);
    return function() { document.removeEventListener('keydown', handler); };
  }, [visible, exiting, close]);

  useEffect(function() {
    if (!visible || exiting || !modalRef.current) return;
    var modal = modalRef.current;
    var focusable = modal.querySelectorAll('a[href], button:not([disabled]), [tabindex]:not([tabindex="-1"])');
    if (!focusable.length) return;
    var first = focusable[0];
    var last = focusable[focusable.length - 1];
    var handler = function(e) {
      if (e.key !== 'Tab') return;
      if (e.shiftKey) { if (document.activeElement === first) { e.preventDefault(); last.focus(); } }
      else { if (document.activeElement === last) { e.preventDefault(); first.focus(); } }
    };
    document.addEventListener('keydown', handler);
    return function() { document.removeEventListener('keydown', handler); };
  }, [visible, exiting]);

  if (!visible) return null;

  var animClass = reducedMotion.current ? '' : (exiting ? 'wc-popup-exit' : 'wc-popup-enter');

  return (
    <div className={'wc-popup-overlay ' + animClass} onClick={function() { close('overlay'); }} data-testid="world-cup-popup-overlay">
      <div ref={modalRef} className={'wc-popup-modal ' + animClass} role="dialog" aria-modal="true" aria-labelledby="wc-popup-headline" aria-describedby="wc-popup-description" onClick={function(e) { e.stopPropagation(); }} data-testid="world-cup-popup-modal">
        <button className="wc-popup-close" onClick={function() { close('x'); }} aria-label={copy.closeLabel} data-testid="world-cup-popup-close">
          <X size={20} />
        </button>
        <div className="wc-popup-layout">
          <div className="wc-popup-content">
            <div className="wc-popup-eyebrow" data-testid="world-cup-popup-eyebrow">
              <Globe size={14} />
              <span>{copy.eyebrow}</span>
            </div>
            <h2 id="wc-popup-headline" className="wc-popup-headline" data-testid="world-cup-popup-headline">{copy.headline}</h2>
            <p id="wc-popup-description" className="wc-popup-description">{copy.description}</p>
            <ul className="wc-popup-indicators">
              <li className="wc-popup-indicator"><span className="wc-popup-indicator-dot" />{copy.ind1}</li>
              <li className="wc-popup-indicator"><span className="wc-popup-indicator-dot" />{copy.ind2}</li>
              <li className="wc-popup-indicator"><span className="wc-popup-indicator-dot" />{copy.ind3}</li>
            </ul>
            <a ref={ctaRef} href={destUrl} className="wc-popup-cta" onClick={handleCTA} data-testid="world-cup-popup-cta">
              {copy.cta}
              <ArrowRight size={18} />
            </a>
            <p className="wc-popup-microcopy">{copy.micro}</p>
          </div>
          <VisualSection />
        </div>
      </div>
    </div>
  );
}
